// Alphabetical ordering for dropdown options.
//
// Used by the sorted select wrappers (SortedMultiselect / SortedSelect2) and,
// via `$sortOptions`, by native <select> templates. Lists that have a natural
// order (months, weekdays, status/priority scales...) should opt out instead of
// being sorted: `:sort="false"` on the wrappers, or simply not calling this.
// Obvious cases (all-numeric labels, status / month / weekday / scale words)
// are detected here and left in their given order automatically.

const collator = new Intl.Collator(undefined, {
  numeric: true, // "Level 2" before "Level 10"
  sensitivity: "base", // case/accent-insensitive
});

// Entries like "Select Team" / "All Departments" / an empty value are
// pseudo-options and must stay at the top rather than be sorted into the list.
const PLACEHOLDER_LABEL = /^\s*(-+\s*)?(select|choose|please select|all)\b/i;

const isPlaceholder = (label, value) =>
  value === "" ||
  value === null ||
  value === undefined ||
  PLACEHOLDER_LABEL.test(label);

// Lists whose order carries meaning must not be alphabetized: numbers/years/
// durations ("1 Month", "12 Months", "2024"), and status / scale / calendar
// vocabularies (Pending → Approved, Low → High, January → December...).
// Applied automatically on top of the explicit `:sort="false"` opt-out.
const NATURAL_ORDER_WORDS = new Set([
  // calendar
  "january", "february", "march", "april", "may", "june", "july", "august",
  "september", "october", "november", "december",
  "jan", "feb", "mar", "apr", "jun", "jul", "aug", "sep", "sept", "oct", "nov", "dec",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "mon", "tue", "tues", "wed", "thu", "thur", "thurs", "fri", "sat", "sun",
  // status / lifecycle
  "active", "inactive", "enabled", "disabled", "pending", "approved", "rejected",
  "draft", "submitted", "completed", "complete", "cancelled", "canceled",
  "in progress", "open", "closed", "paid", "unpaid", "partially paid", "published",
  "unpublished", "resolved", "unresolved", "on hold", "hold", "failed", "success",
  "processing", "new", "expired", "verified", "unverified", "archived", "deleted",
  "present", "absent", "late", "on time", "half day", "full day",
  // yes / no
  "yes", "no", "true", "false", "on", "off",
  // scales
  "low", "medium", "high", "critical", "urgent", "normal", "minor", "major",
  "daily", "weekly", "monthly", "quarterly", "yearly", "annually", "annual",
  "first", "second", "third", "fourth", "fifth",
]);

const isNaturalOrderLabel = (text) => {
  const t = text.trim().toLowerCase();
  if (!t) return true;
  if (/^[\d(+\-–.$₨]/.test(t)) return true; // starts with a digit / sign / currency
  if (/^q[1-4]$/.test(t)) return true; // Q1..Q4
  return NATURAL_ORDER_WORDS.has(t.replace(/[_-]+/g, " "));
};

const defaultLabel = (option, labelKey) => {
  if (option === null || option === undefined) return "";
  if (typeof option !== "object") return option;
  return labelKey ? option[labelKey] : "";
};

/**
 * Returns a new array sorted A–Z by label. Never mutates the input.
 *
 * @param {Array}  list      options (strings, or objects)
 * @param {String|Function} label  key to read the label from, or (option) => label
 * @param {Object} [config]
 * @param {String} [config.valueKey]  key holding the option value; used only to
 *                                    detect an empty "placeholder" entry
 */
export function sortOptions(list, label, config = {}) {
  if (!Array.isArray(list) || list.length < 2) return list;

  const getLabel = (option) => {
    let text;
    try {
      text =
        typeof label === "function"
          ? label(option)
          : defaultLabel(option, label);
    } catch (e) {
      text = "";
    }
    return text === null || text === undefined ? "" : String(text);
  };

  const getValue = (option) =>
    option && typeof option === "object" && config.valueKey
      ? option[config.valueKey]
      : option;

  const pinned = [];
  const rest = [];
  list.forEach((option) => {
    const text = getLabel(option);
    (isPlaceholder(text, getValue(option)) ? pinned : rest).push({
      option,
      text,
    });
  });

  // Numbers, statuses, scales, months... keep the order they were given in.
  if (rest.length && rest.every((el) => isNaturalOrderLabel(el.text))) {
    return list;
  }

  rest.sort((a, b) => collator.compare(a.text, b.text));

  return [...pinned, ...rest].map((el) => el.option);
}

export default sortOptions;
